import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { DsButtonVariants, DsChipColorVariant, Icons, IconStyles } from '@levelaccess/design-system';

import { componentScope } from '../../../../../../../constants/component-scope';
import {
  $comment,
  $manualAuditIssueComment,
  $taskCreateComment,
  $taskDeleteComment,
  commentType,
} from '../../../../../../../../../shared/constants/comment';
import { getFlagReasonTranslationKey } from '../../../../../../../../../shared/utils/comment.utility';
import { SharedCommonUtility } from '../../../../../../../../../shared/utils/common.utility';
import { AuditFindingQAStatusDisplayLabel } from '../../../../../../../../../shared/constants/audit-finding';
import { RuleAuditHistoryService } from '../../../../../../../services/rule-audit-history.service';
import { IManualEvaluationAuditListItem } from '../../../../../../../../../shared/interfaces/manual-audit.interface';
import { ManualEvaluationAuditType } from '../../../../../../../../../shared/constants/manual-audit-issues';
import { TranslateService } from '../../../../../../../translate/translate.service';
import { Api } from '../../../../../../../../../shared/constants/api';
import { CustomValidators } from '../../../../../../../services/helpers/form-custom-validators';

@Component({
  selector: 'app-view-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  standalone: false,
})
export class CommentComponent {
  @Input() public scope: componentScope;
  @Input() public canDelete: boolean;
  @Input() public canEdit: boolean;
  @Input() public comment: IManualEvaluationAuditListItem;
  @Input() public comments: IManualEvaluationAuditListItem[];

  @Output() public commentDeleteEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() public commentEdited: EventEmitter<string> = new EventEmitter<string>();

  public editing: boolean = false;
  public editControl: UntypedFormControl = new UntypedFormControl('', {
    validators: [CustomValidators.required, CustomValidators.validateIsEmpty],
  });
  public editFormValidationRequest$: Subject<void> = new Subject<void>();

  @ViewChild('viewComment') public viewComment?: ElementRef<HTMLButtonElement>;
  @ViewChild('editTextarea') public editTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('editButton', { read: ElementRef }) public editButton?: ElementRef<HTMLButtonElement>;

  public $comment: typeof $comment;
  public $taskCreateComment: typeof $taskCreateComment;
  public $taskDeleteComment: typeof $taskDeleteComment;
  public $manualAuditIssueComment: typeof $manualAuditIssueComment;
  public commentType: typeof ManualEvaluationAuditType;
  public auditFindingQAStatusDisplayLabel: typeof AuditFindingQAStatusDisplayLabel;
  public DsChipColorVariant: typeof DsChipColorVariant = DsChipColorVariant;
  public Icons: typeof Icons = Icons;
  public IconStyles: typeof IconStyles = IconStyles;
  public DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly getFlagReasonTranslationKey: typeof getFlagReasonTranslationKey = getFlagReasonTranslationKey;
  public commentTranslation: string;

  public randomString: string;

  constructor(
    private ruleAuditHistoryService: RuleAuditHistoryService,
    private translateService: TranslateService,
  ) {
    this.$comment = $comment;
    this.commentType = ManualEvaluationAuditType;
    this.auditFindingQAStatusDisplayLabel = AuditFindingQAStatusDisplayLabel;
    this.$manualAuditIssueComment = $manualAuditIssueComment;
    this.$taskCreateComment = $taskCreateComment;
    this.$taskDeleteComment = $taskDeleteComment;
    this.commentTranslation = this.translateService.instant('comment');
    this.randomString = SharedCommonUtility.getRandomInsecureString();
  }

  public delete(): void {
    this.commentDeleteEvent.emit(String(this.comment[$comment._id]));
  }

  public startEdit(): void {
    this.editing = true;
    this.editControl.setValue(this.comment[$manualAuditIssueComment.message] ?? '');
    this.editControl.markAsUntouched();
    if (this.viewComment) {
      this.viewComment.nativeElement.value = 'true';
    }
    setTimeout((): void => {
      this.editTextarea?.nativeElement?.focus();
    });
  }

  public saveEdit(): void {
    this.editFormValidationRequest$.next();
    this.editControl.markAsTouched();
    this.editControl.updateValueAndValidity();
    if (this.editControl.invalid) {
      return;
    }

    this.commentEdited.emit(String(this.editControl.value).trim());
    this.editing = false;
  }

  public cancelEdit(): void {
    this.editing = false;
    this.editControl.reset('');
    setTimeout((): void => {
      this.editButton?.nativeElement?.focus();
    });
  }

  public getUserAndTimestamp(): string {
    return this.ruleAuditHistoryService.getStatusLine(new Date(this.comment[$comment.createdAt]), this.comment[$comment.user]);
  }

  public isRelatedTaskExist(): boolean {
    const hasNoEvents = !Array.isArray(this.comments) || this.comments.length === 0;
    const findSubsequentTaskRemovalEvent = (comment: IManualEvaluationAuditListItem): boolean =>
      comment.type === commentType.taskDelete && new Date(comment.createdAt) > new Date(this.comment.createdAt);
    return hasNoEvents || !this.comments.find(findSubsequentTaskRemovalEvent);
  }

  public get taskLink(): string {
    if (this.comment[$taskCreateComment.projectId] && this.comment[$taskCreateComment.taskId]) {
      return `/${Api.projects}/${this.comment[$taskCreateComment.projectId]}/${Api.tasks}/${
        this.comment[$taskCreateComment.taskId]
      }`;
    }
    return '';
  }
}
